/**
 * The desktop chat's API mode: the AI is reached through its provider's official API (Anthropic's
 * Messages API, or OpenAI's Chat Completions) with the person's own key, and drives Vephus through
 * tools — function calling, not text blocks. The request itself goes through the desktop's Rust side
 * (`ai_request`), which adds the key from the operating system's credential store; this module builds
 * the requests and runs the tool loop.
 *
 * Tools that only read run at once; tools that change something wait for the person to confirm, as
 * the action cards of the CLI mode do. Every result — or the refusal — goes back to the AI.
 *
 * Anthropic: `claude-opus-5-5` by default; thinking is adaptive (always on for that model) with the
 * effort set explicitly; the system prompt is cached; each assistant response is appended unchanged
 * (preserved thinking needs an append-only history); the server-side fallback is on, so a declined
 * request is retried by the API on a fallback model.
 */

import type Anthropic from '@anthropic-ai/sdk'
import { BaseAnthropic } from '@anthropic-ai/sdk/client'
import { Messages as BetaMessages } from '@anthropic-ai/sdk/resources/beta/messages/messages'

export type Provider = 'anthropic' | 'openai'

export const ANTHROPIC_MODELS = ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-4-5'] as const
export const FALLBACK_BETA = 'server-side-fallback-2026-07-01'
const MAX_STEPS = 16

export interface ToolSpec {
  name: string
  description: string
  /** JSON Schema of the input (an object). */
  parameters: { type: 'object'; properties: Record<string, unknown>; required: string[]; additionalProperties: false }
  /** Changes something: the person confirms before it runs. */
  mutating: boolean
}

const str = (description: string) => ({ type: 'string', description })

/** What the AI can do in Vephus — the app's functions, as tools. */
export const VEPHUS_TOOLS: ToolSpec[] = [
  { name: 'list_work_orders', mutating: false, description: 'List the person\'s work orders (once, until-done loops, schedules, team work) with status, phase and round.', parameters: { type: 'object', properties: {}, required: [], additionalProperties: false } },
  { name: 'list_queue', mutating: false, description: 'List the prompt queue (prompts waiting for or run by the desktop app) and whether the desktop app is online.', parameters: { type: 'object', properties: {}, required: [], additionalProperties: false } },
  { name: 'list_repositories', mutating: false, description: 'List the organization\'s linked repositories with their project and team.', parameters: { type: 'object', properties: {}, required: [], additionalProperties: false } },
  { name: 'list_teammates', mutating: false, description: 'List the teammates who can take team work on the active repository, and whether each can take it now.', parameters: { type: 'object', properties: {}, required: [], additionalProperties: false } },
  { name: 'list_live_sessions', mutating: false, description: 'List the agents working now: who, repository, branch, current step, progress.', parameters: { type: 'object', properties: {}, required: [], additionalProperties: false } },
  { name: 'search_docs', mutating: false, description: 'Search the team\'s documentation in Vephus. Returns paths, titles and snippets.', parameters: { type: 'object', properties: { query: str('What to search for.') }, required: ['query'], additionalProperties: false } },
  { name: 'read_doc', mutating: false, description: 'Read one team document from Vephus by its path (e.g. specs/checkout.md).', parameters: { type: 'object', properties: { path: str('Document path, ending in .md.') }, required: ['path'], additionalProperties: false } },
  { name: 'list_files', mutating: false, description: 'List the files of the active repository on this machine (paths only), optionally under a folder.', parameters: { type: 'object', properties: { under: str('Folder prefix, or empty for all.') }, required: ['under'], additionalProperties: false } },
  { name: 'read_file', mutating: false, description: 'Read a text file of the active repository on this machine.', parameters: { type: 'object', properties: { path: str('Path relative to the repository root.') }, required: ['path'], additionalProperties: false } },
  { name: 'open_screen', mutating: false, description: 'Open a screen of the app for the person: /agents/work, /live, /docs, /docs/pinned, /audit, /remote, /agents/repositories, /dashboard.', parameters: { type: 'object', properties: { route: str('The screen\'s path.') }, required: ['route'], additionalProperties: false } },
  { name: 'write_file', mutating: true, description: 'Overwrite an existing text file of the active repository on this machine with new content.', parameters: { type: 'object', properties: { path: str('Path relative to the repository root.'), content: str('The whole new content.') }, required: ['path', 'content'], additionalProperties: false } },
  { name: 'create_work_order', mutating: true, description: 'Send work to the person\'s agent in the active repository: Once (one command), UntilDone (a checker keeps going until nothing is left) or Scheduled (every interval_minutes, 5 to 10080).', parameters: { type: 'object', properties: { kind: { type: 'string', enum: ['Once', 'UntilDone', 'Scheduled'] }, instructions: str('What the agent must do, with the criteria for done.'), interval_minutes: { type: 'integer', description: 'Only for Scheduled.' } }, required: ['kind', 'instructions'], additionalProperties: false } },
  { name: 'start_team_work', mutating: true, description: 'Start team work: the Builder implements and pushes to a branch, the Reviewer finds problems and runs the tests, until the Reviewer says it is done. Builder/reviewer are teammates\' names; leave one empty for the person.', parameters: { type: 'object', properties: { instructions: str('The task and its criteria.'), builder: str('Teammate name, or empty.'), reviewer: str('Teammate name, or empty.') }, required: ['instructions', 'builder', 'reviewer'], additionalProperties: false } },
  { name: 'queue_prompt', mutating: true, description: 'Put a prompt in the person\'s queue: the desktop app runs it in a fresh agent within 30 seconds.', parameters: { type: 'object', properties: { instructions: str('The prompt.') }, required: ['instructions'], additionalProperties: false } },
  { name: 'write_doc', mutating: true, description: 'Write (create or replace) a team document in Vephus. No secrets in it.', parameters: { type: 'object', properties: { path: str('Path ending in .md.'), content: str('Markdown.') }, required: ['path', 'content'], additionalProperties: false } },
  { name: 'pin_doc', mutating: true, description: 'Pin a document as an instruction every agent session starts with.', parameters: { type: 'object', properties: { path: str('Document path.') }, required: ['path'], additionalProperties: false } },
  { name: 'run_audit', mutating: true, description: 'Run an audit on the active repository: audit.quality, audit.security, audit.deploy, audit.performance, audit.architecture, audit.scalability.', parameters: { type: 'object', properties: { key: str('The audit key.') }, required: ['key'], additionalProperties: false } },
]

export const toolByName = (name: string) => VEPHUS_TOOLS.find((t) => t.name === name)

/** Anthropic tool definitions: strict, so the input always matches the schema. */
export function anthropicTools(): Anthropic.Beta.BetaTool[] {
  return VEPHUS_TOOLS.map((t) => ({ name: t.name, description: t.description, input_schema: t.parameters, strict: true }))
}

/**
 * The official SDK's base client with only the resource the chat uses (`beta.messages`): the full
 * client carries every API resource, which put the app over its JavaScript budget. Same requests,
 * same types, same retries — documented as the way to build a smaller client.
 */
class ChatClient extends BaseAnthropic {
  beta = { messages: new BetaMessages(this) }
}

/**
 * The official SDK, with a fetch that hands the request to the desktop's Rust side instead of the
 * network: Rust puts the real key on it and sends it. The SDK never holds the key — the placeholder
 * below is all it sees — and keeps doing what it does well: typed requests and responses, typed
 * errors, retries on 429/5xx.
 */
export function anthropicClient(request: Requester): ChatClient {
  const relay = async (url: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const target = String(url instanceof Request ? url.url : url)
    if (!/\/v1\/messages(\?|$)/.test(target)) throw new Error(`Only /v1/messages goes through the desktop, not ${target}.`)
    const headers = new Headers(init?.headers)
    const beta = headers.get('anthropic-beta') ?? undefined
    const answer = await request('anthropic', typeof init?.body === 'string' ? init.body : '', beta)
    return new Response(answer.body, { status: answer.status, headers: { 'content-type': 'application/json' } })
  }
  return new ChatClient({ apiKey: 'held-by-the-desktop', dangerouslyAllowBrowser: true, fetch: relay as typeof fetch, maxRetries: 2 })
}

export function openaiTools() {
  return VEPHUS_TOOLS.map((t) => ({ type: 'function', function: { name: t.name, description: t.description, parameters: t.parameters } }))
}

/** The request to send, and the provider's answer: status and body, as the Rust side returns them. */
export type Requester = (provider: Provider, body: string, beta?: string) => Promise<{ status: number; body: string }>

export interface LoopDeps {
  request: Requester
  /** Runs a tool and says what happened, in a line or a short text that goes back to the AI. */
  runTool: (name: string, input: Record<string, unknown>) => Promise<string>
  /** Asks the person before a tool that changes something; false means declined. */
  confirm: (name: string, input: Record<string, unknown>) => Promise<boolean>
  /** Shows what the AI says, and each tool it calls, as it goes. */
  onText: (text: string) => void
  onTool: (name: string, input: Record<string, unknown>, result: string) => void
}

export interface Conversation {
  provider: Provider
  model: string
  effort: 'low' | 'medium' | 'high'
  system: string
  /** The provider-shaped history, appended to, never edited. */
  messages: unknown[]
}


/** One person's message, through to the AI's final answer — running tools on the way. */
export async function sendTurn(conversation: Conversation, text: string, deps: LoopDeps): Promise<{ stopReason: string }> {
  return conversation.provider === 'anthropic' ? anthropicTurn(conversation, text, deps) : openaiTurn(conversation, text, deps)
}

async function execute(name: string, input: Record<string, unknown>, deps: LoopDeps): Promise<{ result: string; isError: boolean }> {
  const tool = toolByName(name)
  if (!tool) return { result: `No tool called ${name}.`, isError: true }
  if (tool.mutating && !(await deps.confirm(name, input))) return { result: 'The person declined this action.', isError: true }
  try {
    const result = await deps.runTool(name, input)
    deps.onTool(name, input, result)
    return { result, isError: false }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    deps.onTool(name, input, `failed: ${message}`)
    return { result: `Failed: ${message}`, isError: true }
  }
}

/** An OpenAI error body, as a readable error. */
function fail(status: number, body: string): never {
  let detail = body
  try {
    const parsed = JSON.parse(body) as { error?: { message?: string } }
    detail = parsed.error?.message ?? body
  } catch {
    // Not JSON: the body as it came.
  }
  throw new Error(`${status}: ${detail.slice(0, 400)}`)
}

async function anthropicTurn(c: Conversation, text: string, deps: LoopDeps) {
  const client = anthropicClient(deps.request)
  const messages = c.messages as Anthropic.Beta.BetaMessageParam[]
  messages.push({ role: 'user', content: text })
  for (let step = 0; step < MAX_STEPS; step++) {
    const message = await client.beta.messages.create({
      model: c.model,
      max_tokens: 16000,
      // A declined request is retried by the API on a fallback model chosen by the refusal's category.
      betas: [FALLBACK_BETA],
      fallbacks: 'default',
      // The instructions are the same every turn: cached, so each turn pays for them once.
      system: [{ type: 'text', text: c.system, cache_control: { type: 'ephemeral' } }],
      messages,
      tools: anthropicTools(),
      tool_choice: { type: 'auto' },
      output_config: { effort: c.effort },
    })
    // Appended exactly as it came — thinking blocks included: the model checks its own history.
    messages.push({ role: 'assistant', content: message.content })
    const said = message.content.flatMap((b) => (b.type === 'text' ? [b.text] : [])).join('\n\n')
    if (said) deps.onText(said)

    if (message.stop_reason === 'pause_turn') continue
    // Never run tools from a refused or truncated turn: their input may be cut short.
    if (message.stop_reason !== 'tool_use') return { stopReason: message.stop_reason ?? 'end_turn' }

    // Every tool result of this step in one message, in the order the calls came.
    const results: Anthropic.Beta.BetaToolResultBlockParam[] = []
    for (const block of message.content) {
      if (block.type !== 'tool_use') continue
      const { result, isError } = await execute(block.name, (block.input ?? {}) as Record<string, unknown>, deps)
      results.push({ type: 'tool_result', tool_use_id: block.id, content: result, ...(isError ? { is_error: true } : {}) })
    }
    messages.push({ role: 'user', content: results })
  }
  return { stopReason: 'max_steps' }
}

interface OpenAiMessage {
  role: string
  content: string | null
  tool_calls?: Array<{ id: string; type: string; function: { name: string; arguments: string } }>
}

async function openaiTurn(c: Conversation, text: string, deps: LoopDeps) {
  if (c.messages.length === 0) c.messages.push({ role: 'system', content: c.system })
  c.messages.push({ role: 'user', content: text })
  for (let step = 0; step < MAX_STEPS; step++) {
    const response = await deps.request('openai', JSON.stringify({ model: c.model, messages: c.messages, tools: openaiTools(), tool_choice: 'auto' }))
    if (response.status !== 200) fail(response.status, response.body)
    const choice = (JSON.parse(response.body) as { choices: Array<{ message: OpenAiMessage; finish_reason: string }> }).choices[0]!
    c.messages.push(choice.message)
    if (choice.message.content) deps.onText(choice.message.content)
    if (!choice.message.tool_calls?.length) return { stopReason: choice.finish_reason }
    for (const call of choice.message.tool_calls) {
      let input: Record<string, unknown> = {}
      try {
        input = JSON.parse(call.function.arguments || '{}') as Record<string, unknown>
      } catch {
        c.messages.push({ role: 'tool', tool_call_id: call.id, content: 'Invalid JSON arguments.' })
        continue
      }
      const { result } = await execute(call.function.name, input, deps)
      c.messages.push({ role: 'tool', tool_call_id: call.id, content: result })
    }
  }
  return { stopReason: 'max_steps' }
}

/** The system prompt: the catalog's instructions with the facts of this conversation filled in. */
export function systemFor(template: string, facts: { repository: string; branch: string | null; teammates: string[] }): string {
  return template
    .replace('{{repository}}', facts.repository)
    .replace('{{branch}}', facts.branch ?? 'unknown')
    .replace('{{teammates}}', facts.teammates.join(', ') || 'none')
}
